import {
  documentationQuery,
  getDocumentationDetail,
  getDocumentationMeta,
  getDocumentationRows,
  type DocumentationFilters,
  type DocumentationMeta,
  type DocumentationResponse,
  type PublicDocumentation,
} from "./documentation";

export const usePublicDocumentations = (
  filters: Ref<DocumentationFilters>,
): {
  data: Ref<DocumentationResponse | null>;
  pending: ReturnType<typeof useAsyncData<DocumentationResponse>>["pending"];
  error: ReturnType<typeof useAsyncData<DocumentationResponse>>["error"];
  refresh: ReturnType<typeof useAsyncData<DocumentationResponse>>["refresh"];
  rows: ComputedRef<PublicDocumentation[]>;
  meta: ComputedRef<DocumentationMeta>;
} => {
  const query = computed(() => documentationQuery(filters.value));
  const fetchList = (): Promise<DocumentationResponse> => {
    const { $customFetch } = useNuxtApp();
    return $customFetch<DocumentationResponse>(
      `/public-documentations?${query.value}`,
    );
  };
  const lastSuccessfulRows = ref<PublicDocumentation[]>([]);
  const state = useAsyncData(
    `public-documentations-${query.value}`,
    fetchList,
    { watch: [query], server: true, lazy: false },
  );
  // A cached SSR-side error (e.g. a transient fetch failure) otherwise
  // replays forever for this key on client hydration -- nothing else here
  // gives it a way to recover. Retry once on the client.
  onMounted(() => {
    if (state.error.value) state.refresh();
  });
  watch(
    state.data,
    (response) => {
      if (response !== undefined && response !== null) {
        lastSuccessfulRows.value = getDocumentationRows(response);
      }
    },
    { immediate: true },
  );
  return {
    ...state,
    rows: computed(() => {
      if (state.error.value && lastSuccessfulRows.value.length > 0) {
        return lastSuccessfulRows.value;
      }
      return getDocumentationRows(state.data.value ?? {});
    }),
    meta: computed(() => getDocumentationMeta(state.data.value ?? {})),
  };
};

export const fetchPublicDocumentation = async (
  uuid: string,
): Promise<PublicDocumentation | null> => {
  const { $customFetch } = useNuxtApp();
  const response = await $customFetch<DocumentationResponse>(
    `/public-documentations/${encodeURIComponent(uuid)}`,
  );
  return getDocumentationDetail(response);
};
